import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { deduplicateDomains } from '@/utils/domainDeduplication';

interface Domain {
  id: string;
  name: string;
  symbol?: string;
  description?: string;
}

interface MultiDomainSelectorProps {
  channelId?: string;
  selectedDomainIds: string[];
  onDomainsChange: (ids: string[]) => void;
  maxSelections?: number;
  label?: string;
}

export const MultiDomainSelector = ({
  channelId,
  selectedDomainIds,
  onDomainsChange,
  maxSelections = 10,
  label = 'Select Domains'
}: MultiDomainSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: domains, isLoading } = useQuery({
    queryKey: ['domains', channelId],
    queryFn: async () => {
      let query = supabase
        .from('domains')
        .select('id, name, symbol, description')
        .order('name');

      if (channelId) {
        query = query.eq('channel_id', channelId);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // If no channel filter, deduplicate by name
      if (!channelId && data) {
        return deduplicateDomains(data);
      }
      
      return data as Domain[];
    }
  });

  const filteredDomains = domains?.filter(domain =>
    domain.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    domain.symbol?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleDomain = (domainId: string) => {
    if (selectedDomainIds.includes(domainId)) {
      onDomainsChange(selectedDomainIds.filter(id => id !== domainId));
    } else {
      if (selectedDomainIds.length < maxSelections) {
        onDomainsChange([...selectedDomainIds, domainId]);
      }
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading domains...</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground mt-1">
          {selectedDomainIds.length} of {maxSelections} domains selected
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search domains..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto p-1">
        {filteredDomains?.map((domain) => {
          const isSelected = selectedDomainIds.includes(domain.id);
          const isMaxReached = selectedDomainIds.length >= maxSelections && !isSelected;

          return (
            <button
              key={domain.id}
              type="button"
              onClick={() => toggleDomain(domain.id)}
              disabled={isMaxReached}
              className={cn(
                "relative p-3 rounded-lg border-2 transition-all text-left",
                "hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed",
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
              title={domain.description || domain.name}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{domain.name}</div>
                </div>
                {isSelected && (
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {filteredDomains?.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No domains found matching "{searchQuery}"
        </p>
      )}
    </div>
  );
};
